import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Typography } from 'antd';

import BreadCrumbs from '@/components/shared/BreadCrumbs';
import OfferCard from '@/components/shared/OfferCard';
import PageBackgroundImage from '@/components/shared/PageBackgroundImage';
import Wrapper from '@/components/shared/Wrapper';
import { useGetModulesQuery } from '@/services/course';
import { useGetLessonsQuery } from '@/services/lesson';
import { useGetLessonSavedQuery } from '@/services/savedLessons';
import styled, { css, StyledProp } from '@/styled';

import ActionBar from './ActionBar';
import ChatHandler from './Chats';
import Controls from './Controls';
import Header from './Header';
import LessonArea from './LessonArea';
import Timeline from './Timeline';

function Lesson({ className }: StyledProp) {
  const { id, hash } = useParams();
  const navigate = useNavigate();
  const { data, isFetching } = useGetLessonsQuery(id, { refetchOnMountOrArgChange: true });
  const { isSaved } = useGetLessonSavedQuery(parseInt(id), {
    selectFromResult: ({ data }) => {
      return { isSaved: data };
    }
  });
  const modules = useGetModulesQuery({ courseHash: hash });
  const lessonModule = useMemo(
    () => modules.data?.data.find(module => module.id === data?.id_module),
    [data?.id_module, modules.data?.data]
  );
  const currentLesson = lessonModule?.lessons.find(lesson => lesson.id.toString() === id);

  const crumbs = [
    {
      title: 'Resumo',
      onClick: () => navigate('/resumo')
    },
    {
      title: 'Meus Cursos',
      onClick: () => navigate('/cursos')
    },
    {
      title: (
        <Typography.Text ellipsis className='courseTitle'>
          {data?.course_title}
        </Typography.Text>
      ),
      onClick: () => navigate(`/curso/${data?.hash_course}`)
    },
    {
      title: (
        <Typography.Text ellipsis style={{ maxWidth: 300 }}>
          {lessonModule?.title}
        </Typography.Text>
      )
    }
  ];

  return (
    <div className={className}>
      <PageBackgroundImage src={data?.new_image || data?.image} alt='Imagem de fundo da tela da aula' page='lesson' />
      {data?.chats && <ChatHandler chats={data?.chats} />}
      <Wrapper className='wrapper'>
        {!isFetching && <BreadCrumbs items={crumbs} className='breadcrumbs' />}
        <div className='body'>
          <div className='leftContent'>
            <Header className='title' watched={isSaved} module={lessonModule} lessonTitle={data?.title} />
            <LessonArea />
            <Controls
              status={data?.status}
              isLessonWatched={currentLesson?.watched}
              nextLesson={data?.next}
              previousLesson={data?.previous}
            />
            <ActionBar status={data?.status} />
          </div>
          <div className='rightContent'>
            <Timeline className='desktopTimeline' />
            <OfferCard className='offer' />
          </div>
        </div>
      </Wrapper>
    </div>
  );
}

export default styled(Lesson)(
  ({ theme }) => css`
    position: relative;
    overflow: hidden;
    min-height: 100vh;

    .wrapper {
      z-index: 2;
      position: relative;
      display: flex;
      flex-direction: column;
    }

    .courseTitle {
      max-width: 500px;
    }

    .title {
      margin-bottom: ${theme.space.pxToRem(24)};
    }

    .leftContent {
      width: calc(100% - 300px);

      ${theme.breakpoints.down('md')} {
        width: 100%;
      }
    }

    .body {
      position: relative;
      display: flex;
      flex-direction: row;
      column-gap: ${theme.space.pxToRem(40)};
      height: 100%;

      ${theme.breakpoints.down('md')} {
        flex-direction: column;
      }
    }

    .rightContent {
      .offer {
        width: 100%;
        margin-top: ${theme.space.pxToRem(24)};
      }

      .desktopTimeline {
        ${theme.breakpoints.down('md')} {
          display: none;
        }

        width: 300px;
        max-width: 300px;
      }
    }
  `
);
